import type { TFunction } from 'i18next';
import type { TiebreakCriterion } from '@swiss-magic/shared-types';

// Slugified tiebreak names live under tournament.standings.tiebreak.*
// (mirrors the director-side tournament_form.tiebreak_criteria.*
// pattern). Falls through to the raw canonical name on a miss so
// unknown criteria still render. Extracted from StandingsTable for
// story 110 (the participation page shows the same labels).

export function tiebreakSlug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

export function tiebreakLabel(criterion: TiebreakCriterion, t: TFunction): string {
  const key = `standings.tiebreak.${tiebreakSlug(criterion)}`;
  const translated = t(key);
  return translated === key ? criterion : translated;
}

// Union of integer-tiebreak names across all games — safe because tiebreak
// names are globally unique (a name's integer-ness doesn't differ by game).
const INTEGER_TIEBREAKS: Set<TiebreakCriterion> = new Set(
  ['Number of Wins', 'Number of Games with Black'] as TiebreakCriterion[],
);

const TWO_DECIMAL_TIEBREAKS: Set<TiebreakCriterion> = new Set([
  'Tournament Progress',
]);

/**
 * One display rule for tiebreak values everywhere (standings table,
 * TV standings, participation page): integers plain, Tournament
 * Progress with 2 decimals, everything else 1 decimal — never the
 * raw float ("26.833333333333332").
 */
export function formatTiebreak(
  criterion: TiebreakCriterion,
  value: number,
): string {
  if (INTEGER_TIEBREAKS.has(criterion)) return String(value);
  if (TWO_DECIMAL_TIEBREAKS.has(criterion)) return value.toFixed(2);
  return value.toFixed(1);
}
